import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import AuthenticationPasswordStrengthBubble from "@/Shared/Authentication/Bubbles/AuthenticationPasswordStrengthBubble";
import { useAuthentication } from "@/Slices/authenticationSlice";
import {
  errorMessageForContactName,
  errorMessageForEmail,
  errorMessageForPhoneNumber,
} from "@/Shared/Contacts/Validators/contactsErrorMessagesForContact";
import { useGoogleAdsConversions } from "@/Hooks/useGoogleAdsConversions";
import { useOpenAiAdsConversions } from "@/Hooks/useOpenAiAdsConversions";
import { trackPostHogEvent } from "@/Integrations/trackPostHogEvent";
import type { CreateAccountRequest } from "@freightsimple/generated-dashboard-openapi-client/models/CreateAccountRequest";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { useState } from "react";
import { fireAccountCreatedConversion } from "@/Shared/Authentication/Helpers/authenticationAccountCreatedConversion";
import { authenticationSignInWithPassword } from "@/Shared/Authentication/Helpers/authenticationSignInWithPassword";
import {
  errorMessageForCompanyName,
  errorMessageForPassword,
  errorMessageForShipmentFrequency,
} from "@/Shared/Authentication/Validators/authenticationCreateAccountValidators";
import { useUsersApi2 } from "@/Hooks/useUsersApi2";
import { CreateAccountError } from "@/Apis/CreateAccountError";
import { useAuthenticationAttribution } from "@/Shared/Authentication/Hooks/useAuthenticationAttribution";
import { Button } from "@freightsimple/ui-core/Controls/Button";

interface CreateAccountErrors {
  email: ErrorMessageType;
  password: ErrorMessageType;
  name: ErrorMessageType;
  companyName: ErrorMessageType;
  generateErrorMessage: ErrorMessageType;
}

export interface CreateAccountErrorMessages {
  accountType: ErrorMessageType;
  email: ErrorMessageType;
  password: ErrorMessageType;
  name: ErrorMessageType;
  companyName: ErrorMessageType;
  phoneNumber: ErrorMessageType;
  shipmentFrequency: ErrorMessageType;
  companyCurrency: ErrorMessageType;
}

export const AccountType = {
  Personal: "personal",
  Business: "business",
  Broker: "broker",
} as const;
export type AccountType = (typeof AccountType)[keyof typeof AccountType];

function useCreateAccount(
  prefilledEmail?: string | undefined,
  prefilledName?: string | undefined,
  prefilledCode?: string | undefined,
  onSignInInstead?: () => void,
) {
  const [email, _setEmail] = useState(prefilledEmail ?? "");
  const [accountType, setAccountType] = useState<AccountType>();
  const [password, _setPassword] = useState("");
  const [name, setName] = useState(prefilledName ?? "");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyCurrency, setCompanyCurrency] = useState<
    Currency | undefined
  >();
  const [phoneNumberExtension, setPhoneNumberExtension] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [shipmentFrequency, setShipmentFrequency] = useState<
    undefined | string
  >(undefined);
  const [additionalErrorMessages, setAdditionalErrorMessages] = useState<
    Partial<CreateAccountErrors>
  >({});
  const {
    utmSource,
    utmMedium,
    utmCampaignId,
    utmCampaignName,
    utmTerm,
    utmContent,
    gclid,
    msclkid,
    oppref,
    visitorId,
    referrer,
    landingPage,
    firstTouchReferrer,
    firstTouchLandingPage,
  } = useAuthenticationAttribution();
  const { onAccountCreated: onAccountCreatedGoogle } =
    useGoogleAdsConversions();
  const { onAccountCreated: onAccountCreatedOpenAi } =
    useOpenAiAdsConversions();

  const [generalErrorMessage, setGeneralErrorMessage] =
    useState<ErrorMessageType>();
  const [createAccountInProgress, setCreateAccountInProgress] = useState(false);
  const [checkEmailInProgress, setCheckEmailInProgress] = useState(false);
  const usersApi = useUsersApi2();

  function errorMessageForEmailInCreateAccount(email: string) {
    const baseError = errorMessageForEmail(email);

    if (baseError !== undefined) {
      return baseError;
    }

    return undefined;
  }

  const isPersonalAccount = accountType === AccountType.Personal;

  const baseErrorMessages: CreateAccountErrorMessages = {
    accountType: accountType === undefined ? "Required" : undefined,
    email: errorMessageForEmailInCreateAccount(email),
    password: errorMessageForPassword(password),
    name: errorMessageForContactName(name),
    companyName: errorMessageForCompanyName(companyName, isPersonalAccount),
    phoneNumber: errorMessageForPhoneNumber(phoneNumber),
    shipmentFrequency: errorMessageForShipmentFrequency(
      shipmentFrequency,
      isPersonalAccount,
    ),
    companyCurrency: companyCurrency !== undefined ? undefined : "Required",
  };

  const isValid =
    baseErrorMessages.email === undefined &&
    accountType !== undefined &&
    baseErrorMessages.password === undefined &&
    baseErrorMessages.name === undefined &&
    baseErrorMessages.phoneNumber === undefined &&
    baseErrorMessages.companyName === undefined &&
    baseErrorMessages.shipmentFrequency === undefined &&
    baseErrorMessages.companyCurrency === undefined;

  function shallowMerge(
    obj1: CreateAccountErrorMessages,
    obj2: Partial<CreateAccountErrors>,
  ): CreateAccountErrorMessages {
    const result = { ...obj1 };

    // Only these keys exist on both types; `generateErrorMessage` has no
    // slot in the merged messages and is never set anyway.
    const sharedKeys: (keyof CreateAccountErrors &
      keyof CreateAccountErrorMessages)[] = [
      "email",
      "password",
      "name",
      "companyName",
    ];
    for (const key of sharedKeys) {
      const value = obj2[key];
      if (value !== undefined) {
        result[key] = value;
      }
    }

    return result;
  }

  const errorMessages = shallowMerge(
    baseErrorMessages,
    additionalErrorMessages,
  );

  const { authenticate } = useAuthentication();

  async function isEmailUsed(): Promise<boolean | undefined> {
    setCheckEmailInProgress(true);
    const response = await usersApi.isEmailUsed(email);
    if (response === undefined) {
      setAdditionalErrorMessages((prev) => ({
        ...prev,
        email:
          "Something went wrong validating your email address, please try another one.",
      }));
    }
    if (response) {
      setAdditionalErrorMessages((prev) => ({
        ...prev,
        email: (
          <>
            {"Email address taken. "}
            <Button size="link" inheritFont onClick={onSignInInstead}>
              Sign in instead
            </Button>
            ?
          </>
        ),
      }));
    }
    setCheckEmailInProgress(false);
    return response;
  }

  async function createAccount(): Promise<boolean> {
    setAdditionalErrorMessages({});
    setCreateAccountInProgress(true);
    setGeneralErrorMessage(undefined);

    if (companyCurrency === undefined) {
      throw new Error(
        "companyCurrency is undefined. We should never get here.",
      );
    }

    if (accountType === undefined) {
      throw new Error("accountType is undefined. We should never get here.");
    }

    const referralCode = localStorage.getItem("referralCode") ?? undefined;

    function getCompanyName() {
      if (accountType === AccountType.Personal) {
        return name;
      } else {
        return companyName;
      }
    }

    function getShipmentFrequency() {
      if (accountType === AccountType.Personal) {
        return "SingleShipment";
      } else {
        if (shipmentFrequency === undefined) {
          throw new Error(
            "shipmentFrequency is undefined. We should never get here.",
          );
        }

        return shipmentFrequency;
      }
    }

    try {
      await usersApi.postUsersCreateAccount({
        isBrokerSelected: accountType === AccountType.Broker,
        isPersonalSelected: accountType === AccountType.Personal,
        email,
        password,
        name,
        companyName: getCompanyName(),
        phoneNumber,
        phoneNumberExtension,
        utmSource,
        utmMedium,
        utmCampaignId,
        utmCampaignName,
        utmTerm,
        utmContent,
        gclid,
        msclkid,
        oppref,
        referralCode,
        prefilledCode,
        referrer,
        landingPage,
        firstTouchReferrer,
        firstTouchLandingPage,
        companyCurrency,
        visitorId,
        shipmentFrequency: getShipmentFrequency(),
      } as CreateAccountRequest);
    } catch (error: unknown) {
      if (error instanceof CreateAccountError) {
        console.error(`#### error creating account`, { error });
        const e = error.data;
        if (e.message.toLowerCase().includes("password")) {
          setAdditionalErrorMessages({
            password: (
              <>
                {e.message}
                <AuthenticationPasswordStrengthBubble />
              </>
            ),
          });
          setGeneralErrorMessage(e.message);
        } else if (e.message.toLowerCase().includes("email")) {
          setAdditionalErrorMessages({
            email: <>{e.message}</>,
          });
          setGeneralErrorMessage(e.message);
        } else {
          console.error("Unknown error in createAccount", { e });
          setGeneralErrorMessage(e.message);
        }
      }

      setCreateAccountInProgress(false);
      return false;
    }

    await authenticationSignInWithPassword({
      email,
      password,
      setGeneralErrorMessage,
      authenticate,
      onSignedIn: () =>
        fireAccountCreatedConversion({
          email,
          phoneNumber,
          companyName,
          shipmentFrequency,
          isBusinessAccount: accountType === AccountType.Business,
          onAccountCreatedGoogle,
          onAccountCreatedOpenAi,
        }),
    });

    trackPostHogEvent("account_created", {
      account_type: accountType,
      shipment_frequency: getShipmentFrequency(),
      company_name: getCompanyName(),
      utm_source: utmSource,
    });

    setCreateAccountInProgress(false);
    return true;
  }

  function setEmail(newValue: string) {
    _setEmail(newValue);
    setAdditionalErrorMessages({
      ...additionalErrorMessages,
      email: undefined,
    });
  }

  function setPassword(newValue: string) {
    _setPassword(newValue);
    setAdditionalErrorMessages({
      ...additionalErrorMessages,
      password: undefined,
    });
  }

  return {
    accountType,
    setAccountType,
    email,
    setEmail,
    password,
    setPassword,
    name,
    setName,
    phoneNumber,
    setPhoneNumber,
    phoneNumberExtension,
    setPhoneNumberExtension,
    companyCurrency,
    setCompanyCurrency,
    companyName,
    setCompanyName,
    shipmentFrequency,
    setShipmentFrequency,
    generalErrorMessage,
    createAccount,
    createAccountInProgress,
    isValid,
    errorMessages,
    isEmailUsed,
    checkEmailInProgress,
  };
}

export default useCreateAccount;
